import React, { useState, useRef, useEffect } from 'react';
import { Camera, RotateCcw, Check, X } from 'lucide-react';

const WebcamCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState('');
  const [isWebcamReady, setIsWebcamReady] = useState(false);

  // Iniciar webcam ao montar o componente
  useEffect(() => {
    let mounted = true;

    const startWebcam = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          }
        });
        
        if (!mounted) {
          // Se o componente foi desmontado, parar o stream
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }

        streamRef.current = mediaStream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          setIsWebcamReady(true);
        }
      } catch (err) {
        console.error('Erro ao acessar webcam:', err);
        if (mounted) {
          setError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
        }
      }
    };

    startWebcam();

    // Cleanup: parar webcam ao desmontar
    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []); // Array vazio - executa apenas uma vez

  // Capturar foto
  const capture = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      
      // Parar webcam após capturar
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        setIsWebcamReady(false);
      }
    }
  };

  // Confirmar foto
  const confirmImage = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      handleClose();
    }
  };

  // Tirar nova foto
  const retake = async () => {
    setCapturedImage(null);
    setError('');
    
    // Reiniciar webcam
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      });
      
      streamRef.current = mediaStream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setIsWebcamReady(true);
      }
    } catch (err) {
      console.error('Erro ao reiniciar webcam:', err);
      setError('Não foi possível acessar a câmera novamente.');
    }
  };

  // Fechar componente
  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCapturedImage(null);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Capturar Foto para Reconhecimento Facial</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex flex-col items-center">
          {!capturedImage ? (
            <>
              <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden mb-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 border-4 border-dashed border-blue-500 opacity-50 m-4 rounded-lg"></div>
              </div>
              
              <div className="text-sm text-gray-600 mb-4 text-center">
                Posicione o rosto dentro da área marcada
              </div>

              <button
                onClick={capture}
                disabled={!isWebcamReady}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Camera className="h-5 w-5" />
                Capturar Foto
              </button>
            </>
          ) : (
            <>
              <div className="w-full aspect-video bg-gray-900 rounded-lg overflow-hidden mb-4">
                <img
                  src={capturedImage}
                  alt="Foto capturada"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={retake}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-2"
                >
                  <RotateCcw className="h-5 w-5" />
                  Tirar Nova Foto
                </button>
                <button
                  onClick={confirmImage}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Check className="h-5 w-5" />
                  Confirmar Foto
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebcamCapture;

